/* Writes that could not go out yet.
 *
 * A phone in a warehouse loses signal mid-save. The honest options are to
 * lose what was typed, or to hold it and send it when the signal comes back.
 * This holds it.
 *
 * What it will and will not queue is the whole design:
 *
 * * **Only writes**, and only ones that failed because the network was not
 *   there. A 403 is not a connection problem and must not be retried later
 *   as if it were.
 * * **Never a submit, a cancel or a delete.** Those change the ledger and
 *   are not safe to replay against a document somebody else has moved on.
 *   They are refused while offline and say so.
 * * **In order, and once.** Each entry carries an idempotency key so a
 *   replay that half-succeeded does not double anything.
 *
 * It lives in localStorage so it survives the tab closing, which is the
 * normal way a phone "goes offline".
 */
export type Queued = {
  id: string
  /** The method and path exactly as the client would have sent them. */
  method: 'POST' | 'PUT'
  path: string
  body: string
  /** What to tell the person it was. */
  what: string
  at: number
  tries: number
}

const KEY = 'erp.outbox'
const MAX = 50

/** Anything that moves a document through its lifecycle, or moves several at
 *  once. None of it is replayable against a document somebody else has moved
 *  on, so none of it may wait. */
const NEVER_HELD = ['submit', 'cancel', 'amend', 'make', 'workflow', 'bulk',
                    'import', 'depreciate', 'run', 'match', 'match-one',
                    // A pack's work moves through steps, releases to a client, raises
                    // invoices and moves deadlines: each one a lifecycle move made by the
                    // person at that moment, never replayed later (found 2026-10-09).
                    'work', 'packs']

/** Safe to hold and send later? Submitting is not. */
export function mayQueue(method: string, path: string): boolean {
  if (method !== 'POST' && method !== 'PUT') return false
  // Checked against every segment, not just the last one: `/v1/bulk/Customer/`
  // does not *end* with "bulk", and an earlier version of this rule happily
  // queued a bulk submit because of it.
  const segments = path.split('?')[0]!.split('/').filter(Boolean)
  return !segments.some((s) => NEVER_HELD.includes(s))
}

export function read(): Queued[] {
  try {
    const raw = localStorage.getItem(KEY)
    const parsed = raw ? (JSON.parse(raw) as Queued[]) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    // A private window, or somebody cleared site data. An outbox that cannot
    // be read is an empty one, not a crash.
    return []
  }
}

function write(items: Queued[]) {
  try { localStorage.setItem(KEY, JSON.stringify(items.slice(0, MAX))) } catch { /* nothing to do */ }
}

export function add(entry: Omit<Queued, 'id' | 'at' | 'tries'>): Queued {
  const item: Queued = {
    ...entry,
    id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    at: Date.now(),
    tries: 0,
  }
  write([...read(), item])
  return item
}

export function forget(id: string) {
  write(read().filter((q) => q.id !== id))
}

export function clear() {
  write([])
}

/** Send what is waiting, oldest first, stopping at the first one that fails.
 *
 *  Stopping matters: these are edits to the same documents in the order they
 *  were made, and skipping a failure to send the next one would apply them
 *  out of order.
 */
export async function flush(
  send: (q: Queued) => Promise<void>,
): Promise<{ sent: number; left: number; failed?: string }> {
  let sent = 0
  for (const item of read()) {
    try {
      await send(item)
      forget(item.id)
      sent++
    } catch (e) {
      const items = read().map((q) => (q.id === item.id ? { ...q, tries: q.tries + 1 } : q))
      write(items)
      return { sent, left: items.length, failed: (e as Error).message }
    }
  }
  return { sent, left: read().length }
}
